import { GetMemberApplicantInfo_memberApplicantInfo } from '@frontend/app/queries/types/GetMemberApplicantInfo';

export interface ProductData {
  status: string;
  statusColor: 'blue' | 'green' | 'yellow' | 'red' | 'grey';
  productName: string;
  cost: string;
  showViewRequest: boolean;
  hasOrder: boolean;
}

export const adaptMemberProduct = (
  memberApplicantInfo: GetMemberApplicantInfo_memberApplicantInfo | null | undefined
): ProductData => {
  if (!memberApplicantInfo?.catalogOrderRequest) {
    return {
      status: 'No Product Request',
      statusColor: 'grey',
      productName: '--',
      cost: '--',
      showViewRequest: false,
      hasOrder: false,
    };
  }

  const orderRequest = memberApplicantInfo.catalogOrderRequest;
  const creatorOrder = orderRequest.creatorOrderRequest;

  // Determine status based on order state
  const getStatusInfo = () => {
    if (creatorOrder?.line_items?.length) {
      // Has line items, assume order is pending review
      return { status: 'Review Order', color: 'yellow' as const };
    }
    if (orderRequest.brandCatalogId) {
      // Catalog configured but no order yet
      return { status: 'Catalog Available', color: 'blue' as const };
    }
    return { status: 'Pending Setup', color: 'grey' as const };
  };

  const statusInfo = getStatusInfo();

  // Format product name from line items
  const formatProductName = () => {
    if (!creatorOrder?.line_items?.length) {
      return '--';
    }

    const firstItem = creatorOrder.line_items[0];
    if (creatorOrder.line_items.length === 1) {
      return firstItem.title || firstItem.variant_title || 'Product';
    }

    // Multiple items
    return `${firstItem.title || 'Product'} (+${creatorOrder.line_items.length - 1} more)`;
  };

  // Calculate total cost from line items
  const formatCost = () => {
    if (!creatorOrder?.line_items?.length) {
      return '--';
    }

    const totalCost = creatorOrder.line_items.reduce((total, item) => {
      const itemCost = (item.price || 0) * (item.quantity || 1);
      return total + itemCost;
    }, 0);

    if (totalCost === 0) {
      return 'Free';
    }

    return `$${totalCost.toFixed(2)}`;
  };

  return {
    status: statusInfo.status,
    statusColor: statusInfo.color,
    productName: formatProductName(),
    cost: formatCost(),
    showViewRequest: !!creatorOrder?.line_items?.length,
    hasOrder: !!creatorOrder?.line_items?.length,
  };
};
